import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Empty, Spin, Tag } from 'antd';

import { translate } from '@portal/services/i18n';
import { formatDateTo } from '@portal/utils/format';
import { getNotifications } from '@portal/actions/notification';
import './NotificationList.less';
import DataTable from '@portal/components/DataTable/DataTable';
import { GridRenderCellParams } from '@mui/x-data-grid';
import ThemedText from '@portal/components/ThemedText/ThemedText';

interface NotificationListProps {
  pageSize?: number;
  showPagination?: boolean;
  maxItems?: number;
  className?: string;
  onNotificationClick?: (notification: models.Notification) => void;
}

const NotificationList: React.FC<NotificationListProps> = ({
  pageSize = 10,
  showPagination = true,
  maxItems,
  className = '',
  onNotificationClick,
}) => {
  const dispatch = useDispatch();
  const [pageFilter, setPageFilter] = useState<advancedFilterModels.baseFilter>(
    {
      page: 0,
      pageSize: maxItems || pageSize,
      orderBy: 'createdAt',
      sort: 'desc',
    }
  );

  const notifications = useSelector(
    (state: reducers.rootReducer) => state.notification.list
  );

  const notificationsCount = useSelector(
    (state: reducers.rootReducer) => state.notification.listCount
  );

  const loading = useSelector(
    (state: reducers.rootReducer) => state.notification.loading
  );

  useEffect(() => {
    dispatch(getNotifications(pageFilter));
  }, [dispatch, pageFilter]);

  const columns = [
    {
      field: 'title',
      flex: 2,
      headerName: translate('PAGES.NOTIFICATIONS.COLUMNS.TITLE'),
      renderCell: (params: GridRenderCellParams) => (
        <div className="notification-title">
          {params.row.notification?.title || '-'}
        </div>
      ),
    },
    {
      field: 'message',
      flex: 3,
      headerName: translate('PAGES.NOTIFICATIONS.COLUMNS.MESSAGE'),
      renderCell: (params: GridRenderCellParams) => (
        <div className="notification-message">
          {params.row.notification?.message || '-'}
        </div>
      ),
    },
    {
      field: 'sentAt',
      flex: 1,
      headerName: translate('PAGES.NOTIFICATIONS.COLUMNS.CREATED_AT'),
      renderCell: (params: GridRenderCellParams) =>
        params.row.sentAt
          ? formatDateTo(
              params.row.sentAt,
              translate('APPLICATION.DEFAULT_DATE_FORMAT.LUXON')
            )
          : '-',
    },
    {
      field: 'type',
      flex: 1,
      headerName: translate('PAGES.NOTIFICATIONS.COLUMNS.TYPE'),
      renderCell: (params: GridRenderCellParams) => {
        const typeMap: Record<number, { text: string; color: string }> = {
          11: {
            text: translate('PAGES.NOTIFICATIONS.COLUMNS.LIMITS'),
            color: 'blue',
          },
          31: {
            text: translate('PAGES.NOTIFICATIONS.COLUMNS.TRANSFER'),
            color: 'green',
          },
        };
        const type = params.row.notification?.type;
        const typeInfo = typeMap[type] || {
          text: translate('PAGES.NOTIFICATIONS.COLUMNS.OTHER'),
          color: 'default',
        };
        return <Tag color={typeInfo.color}>{typeInfo.text}</Tag>;
      },
    },
  ];

  if (loading) {
    return (
      <div className={`notification-list ${className}`}>
        <div className="notification-list__loading">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (!notifications || notifications.length === 0) {
    return (
      <div className={`notification-list ${className}`}>
        <Empty
          description={
            <ThemedText>
              {translate('PAGES.NOTIFICATIONS.EMPTY_STATE.TITLE')}
              <br />
              {translate('PAGES.NOTIFICATIONS.EMPTY_STATE.DESCRIPTION')}
            </ThemedText>
          }
        />
      </div>
    );
  }

  const dataSource = maxItems
    ? notifications.slice(0, maxItems)
    : notifications;

  return (
    <div className={`notification-list ${className}`}>
      <DataTable
        columns={columns}
        rows={dataSource}
        page={pageFilter.page}
        pageSize={pageFilter.pageSize}
        rowCount={notificationsCount}
        orderBy={pageFilter.orderBy}
        sort={pageFilter.sort}
        onChange={({ page, pageSize }) => {
          setPageFilter({
            ...pageFilter,
            page: page,
            pageSize: pageSize || pageFilter.pageSize,
          });
        }}
        alignLeft
        hideFooter={!showPagination}
        getRowId={(row) => row.id}
        onRowClick={
          onNotificationClick
            ? (params) => onNotificationClick(params.row.notification)
            : undefined
        }
      />
    </div>
  );
};

export default NotificationList;
